import React, { useState, useEffect, useRef } from "react";
import { object, array, func, bool, number, string } from "prop-types";
import { StoryElement, SocialShare, Link } from "@quintype/components";
import { connect } from "react-redux";
import get from "lodash/get";

import Headline from "../../atoms/Headline";
import PublishedAt from "../../atoms/PublishedAt";
import CustomStoryCard5 from "../../molecules/CustomStoryCard5";
import SocialShareTemplate from "../../molecules/SocialShareTemplate";
import { getCommentsCountForStories } from "../../helper/api";
import MetypeCommentWrapper from "../../MetypeComments";
import StoryTag from "../StoryTag";
import DfpAd from "../../dfp-ad";
import { setMetypeBar } from "../../helper/actions";
import { staticTexts } from "../../utils/Translate";
import BookmarkViewIcon from "../../atoms/BookmarkViewIcon";
import { Share } from "../../atoms/icons/StoryPageIcons";
import ShareAllTemplate from "../../molecules/ShareAllTemplate";

import "./video.m.css";

const VideoStoryTemplate = ({
  story,
  relatedStories,
  metypeConfig,
  setMetypeBar,
  isMetypeBar,
  publisherAttributes,
  jwtToken,
  authHost,
  member,
  scrollIndex,
}) => {
  const [commentsCount, setCommentsCount] = useState(0);
  const [isSocialShare, setSocialShare] = useState(false);
  const [metypeTarget, setMetypeTarget] = useState(null);
  const ref = useRef(null);
  const metypeRef = useRef(null);
  const commentIconRef = useRef(null);
  const sectionName = get(story, ["sections", "0"], "");

  const renderMetype = () => {
    if (commentIconRef.current) {
      const targetNode = commentIconRef.current.id;
      setMetypeTarget(targetNode);
      setMetypeBar({ isMetypeBar: !isMetypeBar });
    }
    setMetypeBar({ isMetypeBar: !isMetypeBar });
  };

  const getFirstVideoElement = story.cards
    .map((item) => item["story-elements"].filter((element) => element.type === "youtube-video"))
    .flat();

  const subHeadlineStyle = `${publisherAttributes.lang}-sub-headline`;

  useEffect(() => {
    const encodedUrl = global.btoa(encodeURIComponent(story.url));

    getCommentsCountForStories(metypeConfig, encodedUrl).then((resp) => {
      if (resp.length) {
        setCommentsCount(resp[0].comments_count);
      }
    });
  }, []);

  const toggleShare = () => setSocialShare(!isSocialShare);

  const handleClickOutside = (event) => {
    if (ref.current && !ref.current.contains(event.target)) {
      setSocialShare(false);
    }
  };

  useEffect(() => {
    if (global.deviceWidth > 876) {
      document.addEventListener("click", handleClickOutside, true);
      return () => {
        document.removeEventListener("click", handleClickOutside, true);
      };
    }
  });

  return (
    <div>
      <div styleName="ad-slot" className="top-ad">
        <DfpAd
          adtype={publisherAttributes === "en" ? "EnPalo_ROS_728x90_T1" : "News_728x90_T1"}
          targetStory={story}
          slotId={`story_${scrollIndex}-t1`}
        />
      </div>
      <div styleName="wrapper">
        <div>
          {getFirstVideoElement.length > 0 && (
            <div styleName="primary-video-wrapper">
              <StoryElement element={getFirstVideoElement[0]} story={story} externalLink={true} />
            </div>
          )}
          {story.subheadline && (
            <Headline
              text={story.subheadline}
              headlineType={16}
              headerLevel={2}
              className="story-sub-heading"
              styleName={subHeadlineStyle}
            />
          )}
          <Headline
            text={story.headline}
            headlineType={35}
            headerLevel={1}
            className={`story-headline ${publisherAttributes.lang}-story-headline`}
          />
          <div styleName="meta-data">
            <PublishedAt date={story["first-published-at"]} pageType="video-story" />
          </div>
          <div styleName="bottom-icons">
            <SocialShare template={SocialShareTemplate} title={story.headline} fullUrl={encodeURI(story.url)} />
            <span styleName="share-button" ref={ref}>
              <span onClick={toggleShare}>
                <Share color1="#eee" color2="#222" />
              </span>
              {isSocialShare && (
                <SocialShare
                  template={ShareAllTemplate}
                  title={story.headline}
                  fullUrl={encodeURI(story.url)}
                  toggleShare={toggleShare}
                />
              )}
            </span>
            {publisherAttributes.showBookmark && (
              <span styleName="bookmark">
                <BookmarkViewIcon story={story} />
              </span>
            )}
          </div>
          {story.tags && (
            <div styleName="tags">
              {sectionName && (
                <Link
                  href={sectionName["section-url"]}
                  styleName="sub-section-name"
                  data-tracking-category="Story Sub Section"
                  data-tracking-action="Click"
                  data-tracking-label={sectionName["display-name"]}
                >
                  {publisherAttributes.lang === "en" ? "Watch more from " : ""}
                  <span styleName="section-name">{sectionName["display-name"]}</span>
                  {publisherAttributes.lang === "bn" ? " থেকে আরও দেখুন" : ""}
                </Link>
              )}
              <StoryTag tags={story.tags} />
            </div>
          )}
          {publisherAttributes.showMetype && !isMetypeBar && (
            <div styleName="comments" ref={commentIconRef} onClick={renderMetype} id={story.id}>
              {commentsCount > 0
                ? `${staticTexts(publisherAttributes.lang).readComments} 
                ${publisherAttributes.lang === "en" ? `(${commentsCount})` : commentsCount.toLocaleString("bn-BD")}`
                : `${staticTexts(publisherAttributes.lang).doComment}`}
            </div>
          )}

          <div ref={metypeRef} className={story.id}>
            {publisherAttributes.showMetype &&
              isMetypeBar &&
              metypeTarget === get(metypeRef, ["current", "classList", "value"], "") && (
                <MetypeCommentWrapper
                  metypeConfig={metypeConfig}
                  storyUrl={story.url}
                  metype={isMetypeBar}
                  onToggleMetype={renderMetype}
                  jwtToken={jwtToken}
                  authHost={authHost}
                  lang={publisherAttributes.lang}
                  member={member}
                />
              )}
          </div>
        </div>
        <div styleName="content-wrapper">
          <div styleName="side-ad">
            <DfpAd
              adtype={publisherAttributes.lang === "en" ? "EnPalo_ROS_336x280_R1" : "News_336x280_R1"}
              targetStory={story}
              slotId={`story_${scrollIndex}-r1`}
            />
          </div>
          <div styleName="next-video">{staticTexts(publisherAttributes.lang).nextVideo}</div>
          {relatedStories.length > 0 &&
            relatedStories
              .slice(0, 4)
              .map(
                (story, index) =>
                  story.story["story-template"] === "video" && (
                    <CustomStoryCard5
                      key={index}
                      story={story.story}
                      className="related-stories"
                      imageType="image4x3"
                      aspectRatio={[4, 3]}
                      headlineType={16}
                      displayStoryIcon={true}
                    />
                  )
              )}
        </div>
        <div styleName="horizontal-ad">
          <DfpAd
            adtype={publisherAttributes.lang === "en" ? "EnPalo_ROS_728x90_B2" : "News_728x90_B2"}
            targetStory={story}
            slotId={`story_${scrollIndex}-b2`}
          />
        </div>
      </div>
    </div>
  );
};

const VideoStory = ({
  story,
  relatedStories,
  metypeConfig,
  setMetypeBar,
  isMetypeBar,
  publisherAttributes,
  domainSlug,
  authHost,
  member,
  jwtToken,
  scrollIndex,
}) => (
  <div className="story-grid">
    <VideoStoryTemplate
      story={story}
      relatedStories={relatedStories}
      metypeConfig={metypeConfig}
      setMetypeBar={setMetypeBar}
      isMetypeBar={isMetypeBar}
      publisherAttributes={publisherAttributes}
      domainSlug={domainSlug}
      authHost={authHost}
      member={member}
      jwtToken={jwtToken}
      scrollIndex={scrollIndex}
    />
  </div>
);

VideoStoryTemplate.propTypes = {
  story: object,
  metypeConfig: object,
  relatedStories: array,
  setMetypeBar: func,
  isMetypeBar: bool,
  publisherAttributes: object,
  jwtToken: string,
  domainSlug: string,
  authHost: string,
  member: object,
  scrollIndex: number,
};

VideoStory.propTypes = {
  story: object,
  metypeConfig: object,
  relatedStories: array,
  setMetypeBar: func,
  isMetypeBar: bool,
  publisherAttributes: object,
  domainSlug: string,
  authHost: string,
  member: object,
  jwtToken: string,
  scrollIndex: number,
};

const mapStateToProps = (state) => ({
  domainSlug: get(state, ["qt", "domainSlug"]),
  metypeConfig: get(state, ["qt", "config", "publisher-attributes", "metypeConfig"], {}),
  isMetypeBar: get(state, ["metypeReducer", "isMetypeBar"], false),
  publisherAttributes: get(state, ["qt", "config", "publisher-attributes"], {}),
  jwtToken: get(state, ["userReducer", "jwt_token"], null),
  authHost: get(state, ["qt", "config", "publisher-attributes", "auth_host"], null),
  member: state.member,
});

const mapDispatchToProps = (dispatch) => ({
  setMetypeBar: (flag) => dispatch(setMetypeBar(flag)),
});

export default connect(mapStateToProps, mapDispatchToProps)(VideoStory);
